/** Sales Desk — Quote Win Analytics + Follow-Up Nudges widgets. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, MailWarning, Send } from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const StatTile = ({ label, value, tone = "text-slate-900" }) => (
  <div className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50/50">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-lg font-heading font-semibold ${tone}`}>{value ?? "—"}</div>
  </div>
);

export function WinAnalyticsPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/sales-desk/win-analytics?limit=15")
      .then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[11px] text-slate-400 py-3">Loading analytics…</div>;
  const o = data.overall || {};

  return (
    <div data-testid="win-analytics-panel">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-4 w-4 text-emerald-700" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
          Quote Win Analytics</div>
        <span className="text-[10px] text-slate-400 ml-auto">from {o.total?.toLocaleString()} historical quotes
          {o.unattributed > 0 ? ` · ${o.unattributed} not yet mapped to a customer` : ""}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <StatTile label="Total quotes" value={o.total?.toLocaleString()} />
        <StatTile label="Delivered" value={o.delivered?.toLocaleString()} />
        <StatTile label="Won" value={o.accepted} tone="text-emerald-700" />
        <StatTile label="Lost" value={o.rejected} tone="text-rose-700" />
        <StatTile label="Avg response" value={o.avg_response_days != null ? `${o.avg_response_days}d` : "—"} />
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-left">
              <th className="px-2 py-1.5 font-semibold">Customer</th>
              <th className="px-2 py-1.5 font-semibold text-right">Quotes</th>
              <th className="px-2 py-1.5 font-semibold text-right">Delivered</th>
              <th className="px-2 py-1.5 font-semibold text-right">Won</th>
              <th className="px-2 py-1.5 font-semibold text-right">Lost</th>
              <th className="px-2 py-1.5 font-semibold text-right">Win rate</th>
              <th className="px-2 py-1.5 font-semibold text-right">Avg response</th>
              <th className="px-2 py-1.5 font-semibold text-right">Quoted value</th>
            </tr>
          </thead>
          <tbody>
            {(data.customers || []).map((c) => (
              <tr key={c.customer} className="border-b border-slate-100 hover:bg-emerald-50/30"
                  data-testid={`win-row-${c.customer.slice(0, 20).replace(/\W+/g, "-")}`}>
                <td className="px-2 py-1.5 font-medium text-slate-800 max-w-[260px] truncate" title={c.customer}>{c.customer}</td>
                <td className="px-2 py-1.5 text-right font-mono">{c.quotes}</td>
                <td className="px-2 py-1.5 text-right font-mono">{c.delivered}</td>
                <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{c.won}</td>
                <td className="px-2 py-1.5 text-right font-mono text-rose-600">{c.lost}</td>
                <td className="px-2 py-1.5 text-right font-mono">{c.win_rate != null ? `${c.win_rate}%` : "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{c.avg_response_days != null ? `${c.avg_response_days}d` : "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtINR(c.total_value)}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function QuoteNudgesPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/quote-nudges/preview")
    .then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/quote-nudges/run");
      toast.success(`${r.sent} reminder(s) emailed to the sales team`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data || (data.items || []).length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4" data-testid="quote-nudges-panel">
      <div className="flex items-center gap-2 mb-2">
        <MailWarning className="h-4 w-4 text-amber-700" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">
          Unopened quotes ({data.items.length}) — {data.nudge_after_days}+ days without a customer open</div>
        <button onClick={runNow} disabled={busy} data-testid="send-nudges-btn"
            className="ml-auto h-7 px-2.5 text-[11px] rounded bg-amber-700 text-white inline-flex items-center gap-1 disabled:opacity-50">
          <Send className="h-3 w-3" /> Email reminders now</button>
      </div>
      <div className="space-y-1">
        {data.items.slice(0, 6).map((n) => (
          <div key={n.track_id} className="flex items-center gap-3 text-[11px] bg-white border border-amber-100 rounded px-2.5 py-1.5"
              data-testid={`nudge-row-${n.code}`}>
            <span className="font-mono font-semibold text-slate-800">{n.code}</span>
            <span className="text-slate-600 flex-1 truncate">{n.customer_name || n.sent_to}</span>
            <span className="text-slate-500">{fmtINR(n.grand_total)}</span>
            <span className="text-amber-700 font-medium">{n.days_unopened}d unopened</span>
          </div>))}
        {data.items.length > 6 && (
          <div className="text-[10px] text-amber-700">+ {data.items.length - 6} more…</div>)}
      </div>
      <div className="text-[10px] text-slate-500 mt-2">
        Reminders are also sent automatically every night to each quote owner.</div>
    </div>
  );
}
